import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, CheckCircle2 } from 'lucide-react';
import { foto } from '../imagenes';

const CORPORATE_IMAGES = [
  '/images/corporate/event_1.jpg', '/images/corporate/event_2.jpg', '/images/corporate/event_3.png',
  '/images/corporate/event_4.png', '/images/corporate/event_5.png', '/images/corporate/event_6.png',
  '/images/corporate/event_7.png', '/images/corporate/event_8.jpg', '/images/corporate/event_9.jpg',
  '/images/corporate/event_10.jpg', '/images/corporate/event_11.jpg', '/images/corporate/event_12.jpg',
  '/images/corporate/event_13.jpg', '/images/corporate/event_14.jpg', '/images/corporate/event_15.jpg',
  '/images/corporate/event_16.jpg', '/images/corporate/event_17.jpg', '/images/corporate/event_18.jpg',
  '/images/corporate/event_19.jpg',
];

const BENEFITS = [
  'Recordación de marca inmediata',
  'Fotos físicas con tu logo',
  'Contenido viral para redes',
  'Experiencias 100% interactivas',
];

const CorporateSection: React.FC = () => {
  const [images, setImages] = useState<string[]>(CORPORATE_IMAGES);
  const [rotations, setRotations] = useState<number[]>([]);

  useEffect(() => {
    const shuffled = [...CORPORATE_IMAGES];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setImages(shuffled);
    setRotations(
      Array.from({ length: shuffled.length * 2 }, () => Math.floor(Math.random() * 12) - 6)
    );
  }, []);

  return (
    <section className="relative overflow-hidden bg-cabina-cream py-24">
      <div
        className="pointer-events-none absolute right-0 top-0 h-full w-1/3 bg-gradient-to-l from-white/20 to-transparent"
        aria-hidden="true"
      />

      <div className="container relative z-10 mx-auto px-6">
        <div className="mb-20 flex flex-col items-center gap-16 lg:flex-row">
          <div className="text-center lg:w-1/2 lg:text-left">
            <span className="mb-8 inline-flex items-center gap-2 rounded-full border border-cabina-red/10 bg-white/80 px-4 py-2 shadow-sm backdrop-blur-sm">
              <Sparkles size={16} className="animate-pulse text-cabina-red" />
              <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-gray-700">
                Activaciones de marca &amp; BTL
              </span>
            </span>

            <h2 className="mb-8 font-serif text-5xl font-bold italic leading-tight text-cabina-dark md:text-7xl">
              Eventos{' '}
              <span className="font-black not-italic text-cabina-red">Empresariales</span>
            </h2>

            <div className="mx-auto mb-10 h-1.5 w-20 rounded-full bg-cabina-red lg:mx-0" />

            <div className="mx-auto max-w-2xl space-y-6 text-lg leading-relaxed text-gray-700 lg:mx-0">
              <p className="text-xl font-medium leading-snug text-cabina-dark">
                Potenciamos tu marca con experiencias que <span className="italic">conectan y cautivan</span>.
              </p>
              <p>
                Especialistas en{' '}
                <span className="font-bold">lanzamientos, activaciones BTL y eventos corporativos</span>{' '}
                de alto impacto. Creamos momentos de conexión real donde tus invitados no solo se
                divierten, sino que se llevan a casa un recuerdo físico de tu marca, garantizando
                una recordación única y duradera.
              </p>

              <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-4 text-left md:grid-cols-2">
                {BENEFITS.map((benefit) => (
                  <div key={benefit} className="group flex items-center gap-3">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-cabina-red/10 transition-colors duration-300 group-hover:bg-cabina-red">
                      <CheckCircle2 size={14} className="text-cabina-red group-hover:text-white" />
                    </span>
                    <span className="text-sm font-bold tracking-tight text-gray-800">{benefit}</span>
                  </div>
                ))}
              </div>

              <p className="pt-6 text-base italic opacity-80">
                Cada detalle está diseñado para que tu identidad corporativa sea la protagonista,
                convirtiendo cada impresión en un embajador de tu marca.
              </p>
            </div>

            <div className="mt-12 flex flex-wrap justify-center gap-5 lg:justify-start">
              <Link
                to="/empresas"
                className="group flex items-center gap-3 rounded-full bg-cabina-red px-10 py-4 text-sm font-bold uppercase text-white shadow-xl transition-all duration-300 hover:bg-cabina-redhover hover:shadow-cabina-red/20"
              >
                Cotizar mi evento
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          <div className="relative lg:w-1/2">
            <div className="relative z-20 transform transition-transform duration-700 hover:scale-[1.02]">
              <div className="absolute -inset-4 -z-10 rounded-full bg-cabina-red/5 blur-3xl" aria-hidden="true" />
              <img
                {...foto('/images/Empresas.png', '(min-width: 640px) 450px, 100vw')}
                alt="Tiras de fotos personalizadas con la marca de clientes corporativos"
                width={450}
                height={560}
                loading="lazy"
                className="mx-auto h-auto w-full max-w-[450px] drop-shadow-2xl"
              />
            </div>
            <div
              className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[120%] w-[120%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-cabina-red/5 opacity-30"
              aria-hidden="true"
            />
          </div>
        </div>

        {/* Cinta de eventos reales */}
        <div className="mt-10 overflow-hidden border-t border-cabina-red/5 py-4 md:py-10">
          <div className="flex animate-slide items-center gap-4 px-4 md:gap-8">
            {[...images, ...images].map((src, index) => (
              <div
                key={`${src}-${index}`}
                className="group relative h-16 flex-none overflow-hidden rounded bg-white/40 shadow-sm transition-all duration-700 hover:z-30 hover:scale-105 md:h-44 md:rounded-xl md:shadow-lg"
                style={{ transform: `rotate(${rotations[index] ?? (index % 2 === 0 ? 2 : -2)}deg)` }}
              >
                <img
                  {...foto(src, '(min-width: 768px) 280px, 110px')}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="h-full w-auto max-w-none object-contain transition-transform duration-1000 group-hover:scale-110"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CorporateSection;
